import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './transaction-ledger-close-group.css'

// COMPONENTS
import * as Icons from '@icons'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import CollapsibleElement from '@components/atoms/collapsable-element'
import CurrencyTextField from '@components/molecules/currency-textfield'
import Divider from '@components/atoms/divider'
import MerchantLogo from '@components/atoms/merchant-logo'
import Spinner from '@components/atoms/spinner'
import { Separator } from '@components/shadcn/separator'
import TabGroup from '@components/molecules/tab-group'

// UTILITIES
import { toast } from 'sonner'
import { createResponseObj, VirtualCardAPI } from '@apis'
import { isNonEmptyString } from '@utils/type-checkers'
import { min } from '@constants'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { toUSD, useCurrencyInput } from '@hooks/inputs'
import { flatten } from '@utils/string-helper'
import { queryClient } from '@/lib/queryClient'
import { getTransactionSpend } from '../helpers'

const isSplitcardCreditTransactionGroup = (transactionId?: string, relatedTransactions?: Transaction[]) => {
    if (transactionId === 'splitcard-credit') return true

    return !!relatedTransactions?.some(transaction => {
        const merchantName = transaction.merchant?.name?.trim().toLowerCase()
        return merchantName === 'splitcard credit'
    })
}

const getSplitcardCreditDisplayAmount = (relatedTransactions?: Transaction[]) => (
    relatedTransactions?.find(transaction => transaction.status === 'Credit')?.amount || 0
)


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    canSplit: boolean
    group: Group
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
    sortedLedger: LedgerLineItem[]
    onDrawerClose: () => void
    onGroupClose: () => void
    handleShare: () => void
    isDemo?: boolean
}

const TransactionLedgerCloseGroup: FC<Props> = (props) => {
    const { canSplit, group, groupStatus, sortedLedger, onDrawerClose, onGroupClose, handleShare, isDemo, ...divProps } = props
    const [viewTab, setViewTab] = useState('Final Payouts')
    const [submittingLedger, setSubmittingLedger] = useState(false)
    const [tempLedger, setTempLedger] = useState<LedgerLineItem[]>([...sortedLedger])

    useEffect(() => {
        setTempLedger([...sortedLedger].map(slot => ({ ...slot })))
    }, [sortedLedger])

    const ledgerTotal = useMemo(() => {
        return sortedLedger.reduce((sum, item) => sum + item.breakdown.reduce((s, b) => s + b.amount, 0), 0)
    }, [sortedLedger])

    const tempLedgerTotal = useMemo(() => {
        return tempLedger.reduce((sum, item) => sum + item.breakdown.reduce((s, b) => s + b.amount, 0), 0)
    }, [tempLedger])

    const allPledgesSubmittable = useMemo(() => {
        if (!tempLedger.length) return false

        const pledgeByUser = group.pledges?.reduce<Record<string, number>>((acc, pledge) => {
            if (pledge.user?.id) acc[pledge.user.id] = pledge.amount
            return acc
        }, {}) || {}

        const allTransactionBreakdownsValid = tempLedger.every(transaction => {
            if (isSplitcardCreditTransactionGroup(transaction.transactionId, group.transactions?.[transaction.transactionId])) {
                return true
            }

            const transactionTotal = transaction.breakdown.reduce((sum, item) => sum + item.amount, 0)
            const expectedSpend = getTransactionSpend(group.transactions?.[transaction.transactionId])
            return transactionTotal === expectedSpend
        })

        const userTotals = tempLedger.reduce<Record<string, number>>((acc, transaction) => {
            transaction.breakdown.forEach(lineItem => {
                if (!lineItem.userId) return
                acc[lineItem.userId] = (acc[lineItem.userId] || 0) + lineItem.amount
            })
            return acc
        }, {})

        const allUserAmountSumsGreaterThanZero = Object.values(userTotals).every(total => total === 0 || total >= 1)
        const allUserAmountSumsWithinPledge = Object.entries(userTotals).every(([userId, total]) => {
            const pledgeAmount = pledgeByUser[userId]
            if (typeof pledgeAmount !== 'number') return true
            return total <= pledgeAmount
        })

        return allTransactionBreakdownsValid && allUserAmountSumsGreaterThanZero && allUserAmountSumsWithinPledge
    }, [group.pledges, group.transactions, tempLedger])

    const { payouts, totalRefund } = useMemo(() => {
        const userPayouts: Record<string, { name: string, pledge: number, spent: number, refund: number, user: User, closedAt?: string }> = {}

        group.pledges?.forEach(pledge => {
            if (pledge.user?.id) {
                const name = pledge.user.givenName ? `${pledge.user.givenName} ${pledge.user.familyName || ''}`.trim() : 'Anonymous'
                const pledgeAmount = pledge.closedAt !== '0001-01-01T00:00:00Z' ? pledge.chargedToCard : pledge.amount

                if (userPayouts[pledge.user.id]) {
                    userPayouts[pledge.user.id].pledge += pledgeAmount
                } else {
                    userPayouts[pledge.user.id] = { name, pledge: pledgeAmount, spent: 0, refund: 0, user: pledge.user as User, closedAt: pledge.closedAt }
                }
            }
        })

        tempLedger.forEach(transaction => {
            transaction.breakdown.forEach(item => {
                if (item.userId && userPayouts[item.userId]) {
                    userPayouts[item.userId].spent += item.amount
                }
            })
        })

        let total = 0
        const payoutsList = Object.values(userPayouts).map(p => {
            const refund = p.pledge - p.spent
            total += Math.max(0, refund)
            return { ...p, refund }
        })

        return { payouts: payoutsList, totalRefund: total }
    }, [group.pledges, group.transactions, tempLedger])

    async function handleSubmit() {
        if (isDemo) {
            toast.info('This action is disabled in the demo')
            return
        }
        setSubmittingLedger(true)

        const groupId = group?.id
        console.log("Remove Pledge Data:", { groupId })
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId })}`
        })

        await VirtualCardAPI.UpdateGroupRequest.fetch(group.id, {
            targetAmount: group.targetAmount,
            ledger: tempLedger.map(transaction => ({
                id: transaction.transactionId,
                breakdown: transaction.breakdown
            }))
        }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })

            onDrawerClose()
            onGroupClose()

            return json('success', { status: 200 })
        }).catch((error) => {
            console.error(error)
        })

        setSubmittingLedger(false)
    }

    function handleUpdateLedger(ledgerSlots: LedgerSlot[], transactionId: string) {
        const updatedTransactions = tempLedger.map((transaction) => {
            const relatedUpdates = ledgerSlots.filter(() => transactionId === transaction.transactionId)

            if (relatedUpdates.length === 0) return transaction
            else return {
                ...transaction,
                breakdown: transaction.breakdown.map((item) => {
                    const match = relatedUpdates.find((u) => u.id === item.id)
                    return match ? { ...item, amount: match.amount } : item
                }),
            }
        })

        setTempLedger(updatedTransactions)
    }

    return (
        <div className='flex flex-col gap-4 transaction-ledger-close-group' {...divProps}>
            <div className='scroll-container'>
                <TabGroup
                    tabs={[
                        { name: 'Review Splits', icon: <Icons.Info width="1rem" height="1rem" /> },
                        { name: 'Final Payouts', icon: <Icons.DollarSquare width="1rem" height="1rem" /> }
                    ]}
                    activeTab={viewTab}
                    onTabChange={setViewTab}
                    className='top-tabs'
                />

                {viewTab === 'Final Payouts' && (
                    <div className='final-payouts-container'>
                        {(groupStatus === 'pending' || ledgerTotal !== group.spendAmount) && <div className="flex flex-row items-center gap-4 info-box p-3" style={{ backgroundColor: 'var(--splitcard-red-100)', padding: '1rem', borderRadius: '0.625rem', border: '1px solid var(--splitcard-red-200)' }}>
                            <Icons.Warning tint="var(--splitcard-red-500)" width="1.5rem" height="1.5rem" style={{ flexShrink: 0 }} />
                            <p className="text-body-2" style={{ color: 'var(--splitcard-red-700)', margin: 0 }}>The final total has changed from {priceFormatter.format(ledgerTotal / 100)} to {priceFormatter.format((group.spendAmount || 0) / 100)}. Update how the cost is split so everyone pays the correct amount.</p>
                        </div>}
                        <div className='total-refund-card'>
                            <p className='total-refund-label'>TOTAL REFUND POOL</p>
                            <h1 className='total-refund-amount'>{priceFormatter.format(totalRefund / 100)}</h1>
                            <p className='total-refund-sub'>To be distributed back to group</p>
                        </div>

                        <Card className='payout-list-card'>
                            <div className='payout-header'>
                                <p className='text-body text-gray-500'><strong>PARTICIPANT</strong></p>
                                <p className='text-body text-gray-500'><strong>NET PAYOUT</strong></p>
                            </div>
                            <div className='payout-list'>
                                {payouts.map((payout, index) => (
                                    <React.Fragment key={payout.user.id}>
                                        <div className='payout-row'>
                                            <div className='flex flex-row items-center gap-4'>
                                                <Avatar user={payout.user} />
                                                <div className='flex flex-col'>
                                                    <p className='text-body'><strong>{payout.name === 'Anonymous' && payout.user.id === group.organizer.id ? 'You' : payout.name}</strong></p>
                                                    <p className='text-body-2 text-gray-400'>Spent {priceFormatter.format(payout.spent / 100)} of {priceFormatter.format(payout.pledge / 100)}</p>
                                                </div>
                                            </div>
                                            <div className='flex flex-col items-end !ml-8'>
                                                {payout.closedAt && payout.closedAt !== '0001-01-01T00:00:00Z' ? (
                                                    <p className='text-body-2 text-red-600'><strong>Left Group</strong></p>
                                                ) : (
                                                    <>
                                                        <p className='text-body' style={{ color: 'var(--splitcard-green-700)', fontWeight: 'bold' }}>+{priceFormatter.format(Math.max(0, payout.refund) / 100)}</p>
                                                        <p className='text-body-2 text-gray-400'>REFUND</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {index < payouts.length - 1 && <Divider style={{ margin: '0.5rem 0' }} />}
                                    </React.Fragment>
                                ))}
                                <Divider style={{ margin: '0.5rem 0' }} />
                                <div className='flex flex-row justify-between'>
                                    <h4>Total Verified</h4>
                                    <h4>{priceFormatter.format(totalRefund / 100)}</h4>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}

                {viewTab === 'Review Splits' && (
                    <>
                        <div className="flex flex-row items-center gap-4 info-box p-3 rounded-lg border border-blue-100" style={{ backgroundColor: 'var(--splitcard-blue-100)', padding: '1rem', borderRadius: '0.625rem', border: '1px solid var(--splitcard-blue-100)' }}>
                            <Icons.Info tint="var(--splitcard-blue-500)" width="1.5rem" height="1.5rem" style={{ flexShrink: 0 }} />
                            <p className="text-body-2" style={{ color: 'var(--splitcard-blue-700)', margin: 0 }}>Tap any transaction to adjust the split logic. Any changes here will immediately update the final payout amounts.</p>
                        </div>
                        <div className='merchant-cell-container'>
                                    {sortedLedger.map((item, index) => (
                                <React.Fragment key={`${item.transactionId}-${index}`}>
                                    <LedgerMerchantCell
                                        canSplit={canSplit}
                                        group={group}
                                        item={item}
                                        tempLedger={tempLedger}
                                        onStateChange={() => { }}
                                        onUpdateLedger={(ledgerSlots) => handleUpdateLedger(ledgerSlots, item.transactionId)}
                                        handleShare={handleShare}
                                        groupStatus={groupStatus}
                                        allPledgesSubmittable={allPledgesSubmittable}
                                    />
                                </React.Fragment>))}
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-row items-end" style={{ gap: '0.625rem', display: 'grid', gridAutoFlow: 'column' }}>
                <Button theme="dark" disabled={!allPledgesSubmittable || submittingLedger || (tempLedgerTotal !== group.spendAmount)} onClick={handleSubmit}>
                    {submittingLedger ? <Spinner indicator='circle' style={{ margin: 'auto' }} /> : (
                        <div className="flex flex-row items-center justify-center gap-2">
                            <Icons.Lock width="1rem" height="1rem" />
                            Confirm & Payout
                        </div>
                    )}
                </Button>
            </div>
        </div>
    )
}

interface LedgerSlot extends BreakdownItem {
    isManuallyUpdated: boolean
}
interface Propss {
    canSplit: boolean
    group: Group
    item: LedgerLineItem
    tempLedger: LedgerLineItem[]
    onStateChange: (isOpen: boolean) => void
    onUpdateLedger: (ledgerSlots: LedgerSlot[]) => void
    handleShare: () => void
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
    allPledgesSubmittable: boolean
}

const LedgerMerchantCell: FC<Propss> = ({ canSplit, group, item, tempLedger, onStateChange, onUpdateLedger, groupStatus, allPledgesSubmittable }) => {
    const { user } = useUser()
    const [isOpen, setIsOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('Weighted')
    const [overMaxUserIds, setOverMaxUserIds] = useState<string[]>([])
    const [ledgerSlots, setLedgerSlots] = useState<LedgerSlot[]>([])
    const originalSlotsRef = useRef<LedgerSlot[] | null>(null)

    const lineItem = useMemo(() => { return tempLedger.find((i) => i.transactionId === item.transactionId) }, [item, tempLedger])
    const sortedBreakdown = useMemo(() => {
        if (!!lineItem?.breakdown) {
            const arr = [...lineItem.breakdown]
            const userIndex = arr.findIndex(item => item.userId === user?.id)
            if (userIndex > -1) {
                const [userItem] = arr.splice(userIndex, 1);
                arr.unshift(userItem);
            }
            return arr as LedgerSlot[]
        }
        return [] as LedgerSlot[]
    }, [lineItem])

    useEffect(() => {
        onUpdateLedger(ledgerSlots)
    }, [ledgerSlots])

    useEffect(() => {
        const slots = [...sortedBreakdown].map(slot => ({ ...slot }))
        setLedgerSlots(slots)
        originalSlotsRef.current = slots.map(slot => ({ ...slot }))

        // For merchants, don't auto-calculate - start with Custom tab
        const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
        if (isMerchant) {
            setActiveTab('Custom')
            const overIds = getOverMaxUserIds(slots)
            setOverMaxUserIds(overIds)
            return
        }

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        const activeAmounts = activeSlots.map(s => s.amount)
        if (activeAmounts.length > 0) {
            const minAmount = Math.min(...activeAmounts)
            const maxAmount = Math.max(...activeAmounts)
            if ((maxAmount - minAmount) <= 1) {
                handleTabChange('Equal')
                return
            }
        }

        const weighted = buildWeightedSplitSlots(slots)
        const matchesWeighted = slots.every((s) => {
            const w = weighted.find(ws => ws.id === s.id)
            return w && Math.abs(s.amount - w.amount) <= 1
        })

        if (!matchesWeighted) {
            setActiveTab('Custom')
            const overIds = getOverMaxUserIds(slots)
            setOverMaxUserIds(overIds)
            return
        }

        handleTabChange('Weighted')
    }, [item.transactionId])

    useEffect(() => {
        if (tempLedger.length === 1) setIsOpen(true)
    }, [tempLedger])

    const { relatedTransactions } = useMemo(() => {
        let relatedTransactions: Transaction[] = []
        if (!!group.transactions) relatedTransactions = group.transactions[item.transactionId]
        return { relatedTransactions }
    }, [item])

    const { merchantName, merchantCategory } = useMemo(() => {
        const merchantName = relatedTransactions?.find(transaction => !!transaction.merchant.name)?.merchant.name.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) || ''
        const merchantCategory = relatedTransactions?.find(transaction => !!transaction.mcc.category)?.mcc.category || 'unknown'

        return { merchantName, merchantCategory }
    }, [relatedTransactions])
    const disableLedgerAction = isSplitcardCreditTransactionGroup(item.transactionId, relatedTransactions)
    const displayTabLabel = disableLedgerAction ? '' : activeTab

    const spendAmount = useMemo(() => {
        let amount = 0
        const latestTransaction = relatedTransactions?.at(-1)
        if (latestTransaction && (latestTransaction.status === 'pending' || latestTransaction.status === 'finalized' || latestTransaction.status === 'Credit')) {
            if (latestTransaction.status === 'Credit') {
                amount = (relatedTransactions?.find(transaction => transaction.status === 'finalized')?.amount || 0) - latestTransaction.amount

                if (amount < 0) {
                    amount = 0
                }
            } else {
                amount = latestTransaction.amount
            }
        }

        return amount
    }, [relatedTransactions])
    const displayAmount = disableLedgerAction
        ? getSplitcardCreditDisplayAmount(relatedTransactions)
        : spendAmount

    function handleStateChange(isOpen: boolean) {
        if (disableLedgerAction && isOpen) return
        setIsOpen(isOpen)
        onStateChange(isOpen)
    }

    function getOverMaxUserIds(newSlots: LedgerSlot[]): string[] {
        const pledgeByUser: Record<string, number> = {}
        group.pledges?.forEach(p => { if (p.user?.id) pledgeByUser[p.user.id] = p.amount })

        const totals: Record<string, number> = {}

        tempLedger.forEach(transaction => {
            if (transaction.transactionId === item.transactionId) return

            transaction.breakdown.forEach(b => {
                if (!b.userId) return
                totals[b.userId] = (totals[b.userId] || 0) + b.amount
            })
        })

        const over: string[] = []

        newSlots.forEach(slot => {
            if (!slot.userId) return

            const total = (totals[slot.userId] || 0) + slot.amount
            const max = pledgeByUser[slot.userId] ?? Infinity

            if (total > max) over.push(slot.userId)
        })

        return over
    }

    function buildEvenSplitSlots(slots: LedgerSlot[]): LedgerSlot[] {
        if (!spendAmount || slots.length === 0) return slots

        const closedSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !!(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        const closedAmount = closedSlots.reduce((sum, s) => sum + s.amount, 0)
        const activeSpendAmount = Math.max(0, spendAmount - closedAmount)

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        if (activeSlots.length === 0) return slots

        const perSlotAmount = Math.floor(activeSpendAmount / activeSlots.length)
        const remainder = activeSpendAmount - perSlotAmount * activeSlots.length

        const highestPledgeAmount = Math.max(...(group.pledges?.map(pledge => pledge.amount) || [0]))
        const highestPledgers = group.pledges?.filter(p => p.amount === highestPledgeAmount && activeSlots.some(s => s.id === p.id)) || []
        const highestPledge = highestPledgers[Math.floor(Math.random() * (highestPledgers.length || 1))]

        return slots.map(slot => {
            if (!activeSlots.some(s => s.id === slot.id)) return slot
            return {
                ...slot,
                amount: perSlotAmount + (slot.id === highestPledge?.id ? remainder : 0),
            }
        })
    }

    function buildWeightedSplitSlots(slots: LedgerSlot[]) {
        if (!spendAmount || slots.length === 0) return slots

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        if (activeSlots.length === 0) return slots

        const totalPledgeAmount = slots.reduce((sum, slot) => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return sum + (pledge?.amount || 0)
        }, 0)

        if (totalPledgeAmount === 0) return buildEvenSplitSlots(slots)

        const weightedSlots = slots.map((slot) => {
            if (!activeSlots.some(s => s.id === slot.id)) return slot

            const pledge = group.pledges?.find(p => p.id === slot.id)
            const pledgeAmount = pledge?.amount || 0
            const share = Math.floor((pledgeAmount / totalPledgeAmount) * spendAmount)
            return { ...slot, amount: share }
        })

        const sumShares = weightedSlots.reduce((sum, s) => sum + s.amount, 0)
        const remainder = spendAmount - sumShares

        if (remainder !== 0) {
            const activeTotalPledge = activeSlots.reduce((sum, s) => sum + (group.pledges?.find(p => p.id === s.id)?.amount || 0), 0)

            if (activeTotalPledge > 0) {
                let distributedRemainder = 0
                const adjustedSlots = weightedSlots.map(slot => {
                    if (!activeSlots.some(s => s.id === slot.id)) return slot

                    const pledge = group.pledges?.find(p => p.id === slot.id)?.amount || 0
                    const exactShare = (pledge / activeTotalPledge) * remainder
                    const intShare = Math.trunc(exactShare)
                    distributedRemainder += intShare
                    return { ...slot, amount: slot.amount + intShare }
                })

                const finalRemainder = remainder - distributedRemainder

                if (finalRemainder !== 0) {
                    const highestPledgeAmount = Math.max(...(activeSlots.map(s => group.pledges?.find(p => p.id === s.id)?.amount || 0)))
                    const highestPledgers = activeSlots.filter(s => (group.pledges?.find(p => p.id === s.id)?.amount || 0) === highestPledgeAmount)
                    const targetId = highestPledgers[0]?.id || activeSlots[0].id

                    return adjustedSlots.map(slot => {
                        if (slot.id === targetId) return { ...slot, amount: slot.amount + finalRemainder }
                        return slot
                    })
                }
                return adjustedSlots
            }
        }

        return weightedSlots
    }

    function updatePledgeSlot(index: number, amount: number) {

        setLedgerSlots(prevSlots => {
            const arr = [...prevSlots]
            arr[index].isManuallyUpdated = true

            const isClosed = (slotId: string) => {
                const pledge = group.pledges?.find(p => p.id === slotId)
                return pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z'
            }

            const otherUnchangedSlots = arr.filter((slot, _index) => _index !== index && !slot.isManuallyUpdated && !isClosed(slot.id))
            const otherChangedSlots = arr.filter((slot, _index) => _index !== index && (!!slot.isManuallyUpdated || isClosed(slot.id)))
            const remainingAmount = Math.max(0, (spendAmount || 0) - (otherChangedSlots.reduce((prev, current) => prev + current.amount, 0) + amount))
            const newAmountForOthers = otherUnchangedSlots.length > 0 ? Math.round(remainingAmount / otherUnchangedSlots.length) : 0

            const nextSlots = arr.map((slot, _index) => {
                if (_index === index) return { ...slot, amount }
                if (!slot.isManuallyUpdated && !isClosed(slot.id)) return { ...slot, amount: newAmountForOthers }
                return slot
            })

            const overIds = getOverMaxUserIds(nextSlots)
            setOverMaxUserIds(overIds)

            return nextSlots
        })
    }

    function handleTabChange(tab: string) {
        setActiveTab(tab)

        let proposed = ledgerSlots
        // We might want to use originalSlots here but originalSlotsRef updates when the transactionId changes
        // or ledger updates.
        // In EditMerchant, we use originalSlotsRef. In CloseGroup, the ledger is passed down.
        // The originalSlotsRef is initialized with sortedBreakdown.
        // We should likely base recalculations on originalSlotsRef to avoid drift,
        // unless manual edits should be preserved when switching back to Custom.

        if (tab === 'Weighted' || (originalSlotsRef.current && tab === 'Weighted')) {
            proposed = buildWeightedSplitSlots(originalSlotsRef.current || ledgerSlots)
        } else if (tab === 'Equal') {
            proposed = buildEvenSplitSlots(originalSlotsRef.current || ledgerSlots)
        } else if (tab === 'Custom') {
            const persistedItem = group.ledger?.find(i => i.transactionId === item.transactionId)
            if (persistedItem?.breakdown) {
                const arr = [...persistedItem.breakdown]
                const userIndex = arr.findIndex(item => item.userId === user?.id)
                if (userIndex > -1) {
                    const [userItem] = arr.splice(userIndex, 1);
                    arr.unshift(userItem);
                }
                proposed = arr.map(slot => ({ ...slot } as LedgerSlot))
            }
        }

        const overIds = getOverMaxUserIds(proposed)
        setOverMaxUserIds(overIds)
        setLedgerSlots(proposed)
    }

    return (
        <CollapsibleElement disabled={disableLedgerAction} state={[isOpen, setIsOpen]} initState="closed" onStateChange={handleStateChange}>
            <div className='flex flex-row items-center justify-between' style={{ cursor: disableLedgerAction ? 'default' : 'pointer', width: '100%', padding: '0.5rem 0' }}>
                <div className='flex flex-row items-center gap-4'>
                    {disableLedgerAction ? (
                        <div
                            className='bubble-xxsml'
                            style={{
                                backgroundColor: 'var(--splitcard-white)',
                                backgroundImage: 'url(/splitcard/symbol/black-and-blue-fallback.png)',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: '1.4rem',
                                boxShadow: 'inset 0 0 0 1px var(--splitcard-gray-100)',
                            }}
                        />
                    ) : (
                        <MerchantLogo merchantCategory={merchantCategory} fill={true} variant="circle" />
                    )}
                    <div className='flex flex-col'>
                        <p className='text-body'><strong>{merchantName}</strong></p>
                        {!!displayTabLabel && <p className='text-body-2 text-gray-400' style={{ textTransform: 'uppercase' }}><strong>{displayTabLabel}</strong></p>}
                    </div>
                </div>
                <div className='flex flex-row items-center gap-4'>
                    {(() => {
                        if (disableLedgerAction) return null

                        const ledgerSlotsSum = item.breakdown.reduce((prev, current) => prev + current.amount, 0)
                        const latestTransaction = relatedTransactions?.at(-1)
                        const isFinalized = latestTransaction?.status === 'finalized' || latestTransaction?.status == "Credit"
                        const needsAdjustment = (isFinalized && ledgerSlotsSum !== spendAmount)
                        const isBalanced = isFinalized && groupStatus === 'pending' && ledgerSlotsSum === spendAmount && !!allPledgesSubmittable

                        if (needsAdjustment) return (
                            <div style={{
                                backgroundColor: 'var(--splitcard-red-100)',
                                border: '2px solid var(--splitcard-red-500)',
                                borderRadius: '1rem',
                                padding: '0.25rem 0.75rem',
                                color: 'var(--splitcard-red-500)',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                            }}>
                                Adjust
                            </div>
                        )

                        if (isBalanced) return (
                            <div style={{
                                backgroundColor: 'var(--splitcard-green-100)',
                                border: '2px solid var(--splitcard-green-600)',
                                borderRadius: '1rem',
                                padding: '0.25rem 0.75rem',
                                color: 'var(--splitcard-green-600)',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                            }}>
                                Adjusted
                            </div>
                        )
                        return null
                    })()}
                    <h4 style={{ margin: 0, color: disableLedgerAction ? 'var(--splitcard-green-600)' : undefined }}>
                        {disableLedgerAction ? '+' : ''}{priceFormatter.format((displayAmount || 0) / 100)}
                    </h4>
                    {!disableLedgerAction && <Icons.Chevron width="1.4rem" height="1.4rem" facing={isOpen ? 'down' : 'up'} />}
                </div>
            </div>

            {!disableLedgerAction && <div className='flex flex-col gap-4 items'>
                {canSplit && <><Separator />

                    <TabGroup
                        tabs={[{ name: 'Weighted' }, { name: 'Equal' }, { name: 'Custom' }]}
                        activeTab={activeTab}
                        light={true}
                        onTabChange={handleTabChange}
                    />

                    {activeTab === 'Weighted' && (
                        <div className="flex flex-row items-center gap-4 info-box bg-blue-100 p-4 rounded-[0.625rem] border border-blue-100">
                            <Icons.Split tint="var(--splitcard-blue-700)" width="1.5rem" height="1.5rem" />
                            <p className="text-body-2 text-blue-700 m-0">Splits are automatically calculated based on each person's contribution to the total pool.</p>
                        </div>
                    )}
                </>}

                {activeTab === 'Custom' && ledgerSlots.length > 1 && (
                    (() => {
                        const currentSlotsTotal = ledgerSlots.reduce((sum, slot) => sum + slot.amount, 0)
                        const targetTotal = spendAmount || 0
                        const diff = currentSlotsTotal - targetTotal
                        const isBalanced = diff === 0

                        if (diff > 0) {
                            return (
                                <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                    <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                    <p className="text-body-2 text-red-500 font-bold m-0">Over: {priceFormatter.format(diff / 100)}</p>
                                </div>
                            )
                        }

                        if (diff < 0) {
                            return (
                                <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                    <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                    <p className="text-body-2 text-red-500 font-bold m-0">Left: {priceFormatter.format(Math.abs(diff) / 100)}</p>
                                </div>
                            )
                        }

                        if (isBalanced) return (
                            <div className="flex flex-row items-center justify-between info-box bg-green-100 py-3 px-4 rounded-[0.625rem] border border-green-100">
                                <p className="text-body-2 text-green-700 font-bold m-0">Perfectly balanced</p>
                                <Icons.CheckSmall tint="var(--splitcard-green-700)" width="1.2rem" height="1.2rem" />
                            </div>
                        )
                        return null
                    })()
                )}

                {ledgerSlots.map((slot, index) =>
                    <SlotCell
                        key={`${item.transactionId}-${slot.id}-${index}`}
                        canSplit={canSplit}
                        slot={slot}
                        index={index}
                        activeTab={activeTab}
                        spendAmount={spendAmount}
                        pledges={group.pledges}
                        tempLedger={tempLedger}
                        overMaxUserIds={overMaxUserIds}
                        onPledgeUpdate={(index, amount) => updatePledgeSlot(index, amount)}
                        group={group}
                        groupStatus={groupStatus}
                    />
                )}
            </div>}
        </CollapsibleElement>
    )
}

interface Propsss {
    index: number
    canSplit: boolean
    activeTab: string
    spendAmount: number | undefined
    slot: LedgerSlot
    pledges: Pledge[] | undefined
    tempLedger: LedgerLineItem[]
    overMaxUserIds: string[]
    onPledgeUpdate: (index: number, amount: number) => void
    group: Group
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
}

const SlotCell: FC<Propsss> = ({ index, canSplit, activeTab, pledges, tempLedger, spendAmount, slot, overMaxUserIds, onPledgeUpdate }) => {
    const { user } = useUser()
    const slotParticipantLedgerSum = tempLedger?.reduce((prev, current) => {
        const slotPledges = current.breakdown
            .filter(b => b.id === slot.id)
            .reduce((innerSum, b) => innerSum + (b.amount || 0), 0);
        return prev + slotPledges
    }, 0)
    const slotPledge = pledges?.find((pledge) => pledge.id === slot.id)
    const slotPledgeAmount = slotPledge?.amount || 0
    const slotPledgeClosed = slotPledge?.closedAt !== '0001-01-01T00:00:00Z'
    const availableFunds = slotPledgeAmount - (slotParticipantLedgerSum || 0)
    const singleTransaction = (tempLedger?.length || 0) === 1
    const [_, slotAmountProps, setSlotAmount] = useCurrencyInput({
        initValue: !!slot.amount ? toUSD(`${(slot.amount) / 100}`, undefined, undefined, true) : '0',
        min: singleTransaction
            ? ((slot.amount > 0 && slot.amount < 1) ? min : 0)
            : ((slotParticipantLedgerSum || 0) - slot.amount <= 0 ? min : 0),
    })
    const isOverMax = overMaxUserIds.includes(slot.userId || '')


    useEffect(() => {
        if (slot.isManuallyUpdated) return
        setSlotAmount(toUSD(`${slot.amount / 100}`, undefined, undefined, true))
    }, [slot.amount, slot.isManuallyUpdated])

    function handleUpdate(e: React.ChangeEvent<HTMLInputElement>) {
        const amount = e.target.value
        setSlotAmount(amount)
        onPledgeUpdate(index, toPennies(amount))
    }

    const stakePercentage = useMemo(() => {
        if (spendAmount && spendAmount > 0) {
            return Math.round((slot.amount / spendAmount) * 100)
        }
        return 0
    }, [slot.amount, spendAmount])

    return (
        <div className={flatten("pledge", isOverMax ? 'over-max' : '')}>
            <Avatar user={slot} />
            <div className="flex flex-col justify-center gap-2" style={{ flex: 2 }}>
                <p className='text-body'><strong>{slot.userId === user?.id ? `You (${slot.givenName || ''} ${slot.familyName || ''})`.trim() : `${slot.givenName || ''} ${slot.familyName || ''}`.trim()}</strong></p>

                <div className="flex flex-row items-center gap-2" style={{ marginTop: '0.2rem' }}>
                    {!slotPledgeClosed
                        ? <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem' }}>
                            <p className='text-body-2' style={{ fontSize: '0.8rem', color: 'var(--splitcard-grey-600)' }}>Bal: <span style={{ color: availableFunds <= 0 ? 'var(--splitcard-red-500)' : '', fontWeight: availableFunds <= 0 ? 'bold' : '' }}>{priceFormatter.format(Math.max(availableFunds / 100, 0))}</span></p>
                        </div>
                        : <p className='text-body-2 text-red-600'><strong>Left Group</strong></p>
                    }
                </div>
            </div>
            <div className='flex flex-col gap-2 items-end flex-1'>
                <CurrencyTextField {...slotAmountProps} disabled={activeTab !== 'Custom' || slotPledgeClosed || !canSplit} onChange={handleUpdate} />
                <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem', width: '100%', textAlign: 'center' }}>
                    <p className='text-body-2' style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--splitcard-grey-500)' }}>{stakePercentage}% stake</p>
                </div>
            </div>
        </div>
    )
}


export default TransactionLedgerCloseGroup
